import Component from '@glimmer/component';
import { action, setProperties, get } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { dropTask, restartableTask } from 'ember-concurrency';
import { service } from '@ember/service';
import { DESK_ICON, SELECTED_GROUP_DESK_ICON } from 'garaje/utils/map/map-icons';
import { AvailabilityStatus } from 'garaje/services/live-map';

/**
 * GroupDeskReservationMap Component
 *
 * Displays a floor map for selecting multiple desks for a group reservation.
 * Adapted from ReservationModalMap but designed for multi-desk, single-day selection.
 *
 * @param {Array<Desk>}     availableDesks      Pre-filtered available desks from Phase 1
 * @param {Array<Floor>}    availableFloors     Floors extracted from availableDesks
 * @param {Array<Employee>} selectedCollaborators  Employees who need desks
 * @param {Date}            selectedDate        The reservation date
 * @param {Array<Desk>}     selectedDesks       Currently selected desks (managed by parent)
 * @param {Function}        onDeskToggle        Callback when desk is clicked: (desk) => void
 * @param {Function}        onFloorChange       Callback when floor is changed: () => void
 * @param {Map<string, AvailabilityStatus>} deskAvailabilityMap  Desk availability map from Phase 1
 */

export default class GroupDeskReservationMapComponent extends Component {
  @service state;
  @service store;

  @tracked isMapReady = false;
  @tracked mapHeight;
  @tracked mapWidth;
  @tracked currentFloor;
  @tracked floors = [];
  @tracked desks = [];

  /**
   * Filter to only floors that have floor plans AND enough desks for all collaborators
   */
  get floorsWithMap() {
    const allFloorsWithMaps = this.floors.filter((floor) => floor.floorPlanUrl);
    const requiredDesks = this.args.selectedCollaborators.length;

    // Count desks per floor from availableDesks (pre-filtered by parent)
    const desksByFloor = new Map();
    if (this.args.availableDesks) {
      for (const desk of this.args.availableDesks) {
        const floorId = get(desk, 'floor.id');
        if (floorId) {
          // eslint-disable-next-line ember/use-ember-get-and-set
          const currentCount = desksByFloor.get(floorId) ?? 0;
          // eslint-disable-next-line ember/use-ember-get-and-set
          desksByFloor.set(floorId, currentCount + 1);
        }
      }
    }

    // Filter to only floors that have enough desks
    const floorsWithEnoughDesks = allFloorsWithMaps.filter((floor) => {
      const floorId = get(floor, 'id');
      // eslint-disable-next-line ember/use-ember-get-and-set
      const deskCount = desksByFloor.get(floorId) ?? 0;
      return deskCount >= requiredDesks;
    });

    return floorsWithEnoughDesks;
  }

  /**
   * Get desks on the currently selected floor that are placed on the map
   * AND are actually available for booking (checked via passed availability map)
   */
  get placedDesks() {
    if (!this.currentFloor) {
      return [];
    }

    // First filter for placed and non-deleted desks
    const placedDesks = this.desks.filter((desk) => get(desk, 'placed') && !get(desk, 'isDeleted'));

    // Then filter to only show desks that are AVAILABLE according to passed availability map
    return placedDesks.filter((desk) => {
      const deskId = get(desk, 'id');
      // eslint-disable-next-line ember/use-ember-get-and-set
      const availabilityStatus = this.args.deskAvailabilityMap?.get(deskId);

      // Only show desks that are explicitly AVAILABLE
      // If no status is found, exclude the desk to be safe
      return availabilityStatus === AvailabilityStatus.AVAILABLE;
    });
  }

  /**
   * Apply icons to desks based on selection state
   */
  get placedDesksWithIcons() {
    const selectedDeskIds = new Set(this.args.selectedDesks.map((d) => get(d, 'id')));

    return this.placedDesks.map((desk) => {
      const deskId = get(desk, 'id');
      const isSelected = selectedDeskIds.has(deskId);

      if (isSelected) {
        this.setDeskIcon(desk, SELECTED_GROUP_DESK_ICON);
      } else {
        this.setDeskIcon(desk, DESK_ICON);
      }
      return desk;
    });
  }

  /**
   * Set icon properties on a desk object
   */
  setDeskIcon(desk, icon) {
    setProperties(desk, {
      iconClassname: icon.iconClassname,
      iconSize: icon.iconSize,
      iconHtml: '',
    });
  }

  /**
   * Load floors and set initial floor to the one with most available desks
   * Copied from ReservationModalMap, enhanced to select floor with most desks
   */
  @dropTask
  *loadFloorsTask() {
    const floors = yield this.state.currentLocation.floors;
    this.floors = floors;
    if (floors && this.floorsWithMap.length > 0) {
      // Count desks per floor to find the one with most desks
      const desksByFloor = new Map();
      if (this.args.availableDesks) {
        for (const desk of this.args.availableDesks) {
          const floorId = get(desk, 'floor.id');
          if (floorId) {
            // eslint-disable-next-line ember/use-ember-get-and-set
            const currentCount = desksByFloor.get(floorId) ?? 0;
            // eslint-disable-next-line ember/use-ember-get-and-set
            desksByFloor.set(floorId, currentCount + 1);
          }
        }
      }

      // Find floor with maximum desks
      let maxDesks = 0;
      let floorWithMostDesks = this.floorsWithMap[0];

      for (const floor of this.floorsWithMap) {
        const floorId = get(floor, 'id');
        // eslint-disable-next-line ember/use-ember-get-and-set
        const deskCount = desksByFloor.get(floorId) || 0;
        if (deskCount > maxDesks) {
          maxDesks = deskCount;
          floorWithMostDesks = floor;
        }
      }

      this.currentFloor = floorWithMostDesks;
    } else {
      this.currentFloor = null;
    }
  }

  /**
   * Load available desks for current floor from the backend
   * Adapted from ReservationModalMap - loads desks dynamically based on current floor
   */
  @restartableTask
  *loadAvailableDesksTask() {
    if (!this.currentFloor) {
      return;
    }

    const params = {};
    params.filter = {
      'location-id': get(this.state.currentLocation, 'id'),
    };

    // Load all desks for location, then filter to current floor
    const desks = yield this.store.query('desk', params);
    this.desks = desks.filter((desk) => get(desk, 'floor.id') === this.currentFloor?.id && desk.enabled);
  }

  /**
   * Load map image when component is inserted
   */
  @dropTask
  *onModalLoad() {
    yield this.loadFloorsTask.perform();
    yield this.loadAvailableDesksTask.perform();
    yield this.loadMapImageTask.perform();
  }

  /**
   * Load the floor plan image and get its dimensions
   * Matches ReservationModalMap pattern exactly
   */
  @dropTask
  *loadMapImageTask() {
    if (!this.currentFloor) {
      return;
    }

    yield new Promise((resolve, reject) => {
      const image = new Image();
      image.src = this.currentFloor.floorPlanUrl;
      image.onerror = (err) => {
        // eslint-disable-next-line no-console
        console.error('error loading map', err);
        reject();
      };
      image.onload = (ev) => {
        if (!this.isDestroyed && !this.isDestroying) {
          const { height: mapHeight, width: mapWidth } = ev.target;

          setProperties(this, {
            mapHeight,
            mapWidth,
          });
        }

        resolve();
      };
    });
  }

  /**
   * Handle floor change - reload desks and map image for new floor, clear selections
   * Adapted from ReservationModalMap - reloads data on floor change
   */
  @dropTask
  *onFloorChange(floor) {
    this.currentFloor = floor;
    // Clear selected desks when switching floors
    if (this.args.onFloorChange) {
      this.args.onFloorChange();
    }
    yield this.loadAvailableDesksTask.perform();
    yield this.loadMapImageTask.perform();
  }

  /**
   * Set map ready state
   */
  @action
  toggleIsMapReady() {
    this.isMapReady = true;
  }
}
